import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, AlertDescription } from './Alert';
import { cn } from '../../utils/cn';

/**
 * CO-49 — inline error surface for write dialogs.
 *
 * The defect this closes: a refused write (billing access ladder, RBAC, or a
 * validation reject) left the dialog open with the typed value and rendered
 * NOTHING inside it. The only signal was a corner toast that auto-dismisses,
 * so a user who looked away read the product as broken.
 *
 * The rule this component encodes: a dialog that refused a submit must say so
 * INSIDE itself, persistently, and must name the reason. The toast stays — it
 * is the peripheral signal — but it is never the only one.
 *
 * Usage:
 *   const dialogError = useDialogError();
 *   ...
 *   dialogError.clear();
 *   try { await save(); } catch (err) { dialogError.capture(err); return; }
 *   ...
 *   <DialogBody>
 *     <DialogErrorAlert error={dialogError.error} />
 *     ...fields
 *   </DialogBody>
 */

export type WriteErrorKind =
  | 'billing'        // subscription_restricted — payment lapsed, writes blocked
  | 'featureLocked'  // feature_locked — tier does not include this
  | 'permission'     // 403 / PermissionDenied — this user may not do this
  | 'validation'     // 400 / 422 — the submitted data is not acceptable
  | 'conflict'       // 409 — someone else changed it first
  | 'network'        // no response reached us
  | 'server'         // 5xx
  | 'unknown';

export interface DialogWriteError {
  kind: WriteErrorKind;
  /** Already-localized, user-facing lines. Never raw codes. */
  messages: string[];
  /** In-app path to the billing surface, when the backend supplied one. */
  payUrl?: string;
  /** Whether the current user is allowed on the billing surface at all. */
  canPay: boolean;
}

const BILLING_ROLES = ['Hr', 'Admin'];

type AppStoreLike = { getState?: () => { user?: { role?: string } } };

function currentRole(): string {
  try {
    const store = (globalThis as unknown as { __APP_STORE__?: AppStoreLike }).__APP_STORE__;
    const role = store?.getState?.()?.user?.role;
    return typeof role === 'string' ? role : '';
  } catch {
    return '';
  }
}

type LooseError = Record<string, unknown> & {
  [k: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
} | null | undefined;

function errorList(err: LooseError): Array<Record<string, unknown>> {
  const candidates = [
    err?.errors,
    err?.payload?.errors,
    err?.response?.data?.errors,
    err?.response?.errors,
    err?.data?.errors,
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length) return c.filter(Boolean);
  }
  return [];
}

/**
 * CO-56 repair — TRANSPORT status first, body-reported status last.
 *
 * `__httpStatus` is stamped by the axios error interceptor (`utils/requestHandling.ts`)
 * and is the only field that means "the HTTP response line said this". Everything
 * after it is a body field, and this server's `Result<TPayload>.Status` DEFAULTS TO
 * 200 — so a 403 carrying a default-constructed Result body self-reports 200. Hence
 * the second rule: a body-level status below 400 is not evidence of anything on an
 * error path and is discarded, letting classification fall through to `errors[]`
 * instead of being pinned to a bogus success code.
 */
function httpStatus(err: LooseError): number | undefined {
  const transport = err?.__httpStatus ?? err?.response?.__httpStatus;
  if (typeof transport === 'number') return transport;

  const s = err?.response?.status ?? err?.status ?? err?.statusCode ?? err?.response?.data?.status;
  return typeof s === 'number' && s >= 400 ? s : undefined;
}

function hasCode(err: LooseError, codes: string[]): boolean {
  const top = typeof err?.error === 'string' ? err.error : '';
  if (codes.includes(top)) return true;
  return errorList(err).some((e) => codes.includes(String(e?.code ?? '')));
}

function isNetworkShape(err: LooseError): boolean {
  if (!err) return false;
  if (err.response) return false;
  const msg = String(err.message ?? '').toLowerCase();
  return (
    !!err.request ||
    err.code === 'ECONNREFUSED' ||
    err.code === 'ENOTFOUND' ||
    msg.includes('network') ||
    msg.includes('failed to fetch')
  );
}

/** Decide WHY the write was refused. Order matters: most specific first. */
export function classifyWriteErrorKind(err: LooseError): WriteErrorKind {
  if (hasCode(err, ['subscription_restricted', 'SubscriptionRestricted'])) return 'billing';
  if (hasCode(err, ['feature_locked', 'FeatureLocked'])) return 'featureLocked';
  if (hasCode(err, ['PermissionDenied', 'Forbidden', 'AccessDenied', 'NotAuthorized'])) return 'permission';

  // CO-56: the status MUST be consulted BEFORE isNetworkShape(). A bodiless
  // 403/409/5xx reaches us as a status-only shape, and any shape that also
  // carries `request` (or no message) otherwise matched the network probe
  // first and short-circuited — so a genuine RBAC refusal read as
  // "check your connection". A true offline failure has NO status at all, so
  // it still falls through to the network branch below.
  const status = httpStatus(err);
  if (status === 403 || status === 401) return 'permission';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (typeof status === 'number' && status >= 500) return 'server';

  if (isNetworkShape(err)) return 'network';

  // A Result<T> that came back 200 with errors[] is, by convention here, a
  // rejected write with a business reason: treat it as validation.
  if (errorList(err).length) return 'validation';
  return 'unknown';
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

function localizeCode(code: string, t: TFunc): string {
  if (!code) return '';
  const key = `errors:${code}`;
  const localized = t(key);
  // i18next returns the key or the bare code when the translation is missing,
  // depending on fallbackNS. Treat both as "no translation".
  if (!localized || localized === key || localized === code) return '';
  return localized;
}

/** Pull user-facing lines out of any error shape the API layer can produce. */
export function extractWriteErrorMessages(err: LooseError, t: TFunc): string[] {
  const list = errorList(err);
  const lines = list
    .map((e) => localizeCode(String(e?.code ?? ''), t) || e?.description || e?.message || '')
    .map((s: unknown) => String(s).trim())
    .filter(Boolean);
  if (lines.length) return Array.from(new Set(lines));

  const raw =
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    err?.description ||
    (typeof err?.message === 'string' ? err.message : '');
  const single = String(raw ?? '').trim();
  return single ? [single] : [];
}

/**
 * Turn any thrown/returned API error into a fully-localized inline error.
 * `fallback` is the dialog-specific "we couldn't save this" line.
 */
export function classifyWriteError(err: unknown, t: TFunc, fallback?: string): DialogWriteError {
  const e = (err ?? null) as LooseError;
  const kind = classifyWriteErrorKind(e);
  let messages = extractWriteErrorMessages(e, t);

  // For the kinds where the backend body is either absent or unhelpfully terse,
  // the reason line IS the message. Never leave the user with a bare code.
  if (!messages.length) {
    const byKind: Record<WriteErrorKind, string> = {
      billing: t('dialogError.billingBody'),
      featureLocked: t('dialogError.featureLockedBody'),
      permission: t('dialogError.permissionBody'),
      validation: t('dialogError.validationBody'),
      conflict: t('dialogError.conflictBody'),
      network: t('dialogError.networkBody'),
      server: t('dialogError.serverBody'),
      unknown: fallback || t('dialogError.unknownBody'),
    };
    messages = [byKind[kind]];
  }

  const rawPayUrl = e?.payUrl ?? e?.response?.data?.payUrl;
  const payUrl = typeof rawPayUrl === 'string' && rawPayUrl.startsWith('/') ? rawPayUrl : undefined;

  return {
    kind,
    messages,
    payUrl,
    canPay: BILLING_ROLES.includes(currentRole()),
  };
}

/** Dialog-local error state. One per dialog. */
export function useDialogError() {
  const { t } = useTranslation();
  const [error, setError] = React.useState<DialogWriteError | null>(null);

  const capture = React.useCallback(
    (err: unknown, fallback?: string) => {
      const parsed = classifyWriteError(err, t, fallback);
      setError(parsed);
      return parsed;
    },
    [t]
  );

  /**
   * Client-side validation messages use the same surface, so a user never has
   * to learn two places to look for "why didn't this save".
   */
  const showMessages = React.useCallback((messages: string[], kind: WriteErrorKind = 'validation') => {
    setError({ kind, messages: messages.filter(Boolean), canPay: false });
  }, []);

  const clear = React.useCallback(() => setError(null), []);

  return { error, capture, showMessages, clear, setError };
}

const KIND_STYLE: Record<WriteErrorKind, { variant: 'error' | 'warning'; titleKey: string }> = {
  billing: { variant: 'warning', titleKey: 'dialogError.billingTitle' },
  featureLocked: { variant: 'warning', titleKey: 'dialogError.featureLockedTitle' },
  permission: { variant: 'warning', titleKey: 'dialogError.permissionTitle' },
  validation: { variant: 'error', titleKey: 'dialogError.validationTitle' },
  conflict: { variant: 'warning', titleKey: 'dialogError.conflictTitle' },
  network: { variant: 'warning', titleKey: 'dialogError.networkTitle' },
  server: { variant: 'error', titleKey: 'dialogError.serverTitle' },
  unknown: { variant: 'error', titleKey: 'dialogError.unknownTitle' },
};

export interface DialogErrorAlertProps {
  error: DialogWriteError | null;
  className?: string;
  /** Optional dismiss affordance. Omit to keep the message until the next attempt. */
  onDismiss?: () => void;
}

/**
 * Persistent, in-dialog explanation of a refused write. Renders nothing when
 * there is no error, so it can sit unconditionally at the top of a DialogBody.
 */
export function DialogErrorAlert({ error, className, onDismiss }: DialogErrorAlertProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!error || !error.messages.length) return null;

  const { variant, titleKey } = KIND_STYLE[error.kind] ?? KIND_STYLE.unknown;
  const showPay = error.kind === 'billing' && error.canPay;
  const payUrl = error.payUrl || '/settings/billing';

  return (
    <Alert
      variant={variant}
      className={cn('mb-4', className)}
      onClose={onDismiss}
      closeLabel={t('dialogError.dismiss')}
      data-testid="dialog-error"
      data-error-kind={error.kind}
    >
      <AlertTitle>{t(titleKey)}</AlertTitle>
      {error.messages.map((message, i) => (
        <AlertDescription key={i} className={i > 0 ? 'mt-1' : undefined}>
          {message}
        </AlertDescription>
      ))}
      {error.kind === 'billing' && (
        <AlertDescription className="mt-1 opacity-90">
          {t('dialogError.billingDataPromise')}
        </AlertDescription>
      )}
      {showPay && (
        <button
          type="button"
          onClick={() => navigate(payUrl)}
          className="mt-3 inline-flex items-center rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="dialog-error-pay"
        >
          {t('dialogError.payNow')}
        </button>
      )}
    </Alert>
  );
}

export default DialogErrorAlert;
